import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary'
import { ProtectedRoute } from './components/ProtectedRoute'
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage'
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage'
import { OAuthSuccessPage } from './pages/auth/OAuthSuccessPage'
import { OAuthErrorPage } from './pages/auth/OAuthErrorPage'

// apps/dashboard is the public SSO login UI at login.nugui.co. Everything an
// unauthenticated visitor to /auth/login does NOT need — the dashboard chrome
// and every authenticated page — is route-level code-split via React.lazy so
// the login entry only downloads the auth chunk. Every /auth/* page (plus
// ProtectedRoute, which just gates on auth state) stays a static import above:
// a broken lazy boundary here is a "nobody can log in" incident, so the
// smallest, most auth-critical surface is the one part that can never suspend.
const Layout = lazy(() =>
  import('./components/Layout').then((m) => ({ default: m.Layout })),
)
const MFASetupPage = lazy(() =>
  import('./pages/auth/MFASetupPage').then((m) => ({ default: m.MFASetupPage })),
)
const TenantSetupPage = lazy(() =>
  import('./pages/TenantSetupPage').then((m) => ({ default: m.TenantSetupPage })),
)
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const UsersPage = lazy(() =>
  import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })),
)
const TenantsPage = lazy(() =>
  import('./pages/TenantsPage').then((m) => ({ default: m.TenantsPage })),
)
const BillingPage = lazy(() =>
  import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const SecurityPage = lazy(() =>
  import('./pages/SecurityPage').then((m) => ({ default: m.SecurityPage })),
)
const ApiKeysPage = lazy(() =>
  import('./pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
)
const TenantProvisioningPage = lazy(() =>
  import('./pages/TenantProvisioningPage').then((m) => ({
    default: m.TenantProvisioningPage,
  })),
)

// Full-page loading state for a lazy chunk fetch. Mirrors ProtectedRoute's own
// spinner styling so there is never a blank screen while a route chunk loads.
function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/*
          Suspense covers a chunk that is still loading; the boundary covers one
          that will never load. Splitting the routes means a post-deploy nav can
          request a hashed chunk the new container no longer serves, and a
          rejected lazy import unmounts the React root rather than showing this
          fallback. The boundary reloads once to pick up the current asset
          hashes. Order matters: it must sit outside Suspense to catch it.
        */}
        <ChunkErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
            {/*
              Public routes — eagerly bundled into the login entry chunk.

              reset-password / verify-email / accept-invite are flat,
              query-param routes (?token=...), NOT nested under /auth with a
              :token path param — they must match the URLs the management
              service actually emails (services/management/src/routes/
              password-reset.ts, email-verification.ts, users.ts), which use
              `?token=` for the same reason /setup below already does: it
              keeps the token out of access logs' path component and out of
              route config, and out of sync once already cost a working flow
              (#407).
            */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/auth/oauth-success" element={<OAuthSuccessPage />} />
            <Route path="/auth/oauth-error" element={<OAuthErrorPage />} />
            <Route path="/setup" element={<TenantSetupPage />} />

            {/* Protected routes — lazy-loaded; an unauthenticated visitor never fetches these chunks */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/tenants" element={<TenantsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/api-keys" element={<ApiKeysPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/security/mfa-setup" element={<MFASetupPage />} />
                <Route path="/tenants/new" element={<TenantProvisioningPage />} />
              </Route>
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
